import React, { useCallback, useEffect, useState } from 'react';
import { FieldInputProps, useField } from 'formik';
import styled from 'styled-components';

import type { SearchType } from '@lt/api/dist/types/searchParams';
import { useFetchUnavailableDates, useFetchVacationOptionsPresets } from '../../hooks';
import DatePicker, { type DatePickerState } from '../DatePicker';
import { addStep, useCigarette, type Values } from '../../context';
import { FIELD_NAMES } from '../../constants';
import { afterDatepickerStep, isDatepickerStep, isTouristsStep } from '../../utils';
import { useFetchReferencesSearchCalendarDuration } from '../../hooks/useFetchReferencesSearchCalendarDuration';
import { getIsFeatureHasAnOption } from '../../../../utils';

type FormDatePickerProps = {
  className?: string;
  searchType: SearchType;
  variant?: 'default' | 'hotel';
  isOpenInitial?: boolean;
} & FieldInputProps<DatePickerState>;

export const FormDatePicker = ({
  name,
  className,
  searchType,
  variant = 'default',
  isOpenInitial = false,
}: FormDatePickerProps): JSX.Element | null => {
  const [{ value }, { error }, { setValue, setError }] = useField<Values['datepicker']>(name);
  const [{ value: destinationValue }] = useField<Values['destination']>(FIELD_NAMES.destination);
  const [{ value: departureValue }] = useField<Values['departure']>(FIELD_NAMES.departure);
  const [{ value: touristsValue }] = useField<Values['tourists']>(FIELD_NAMES.tourists);
  const {
    state: { isMobile, isWizard, steps },
    dispatch,
  } = useCigarette();
  const [hide, setHide] = useState(!isMobile);
  const [previousSearchType, setPreviousSearchType] = useState<SearchType | undefined>(undefined);

  const isVacationOptionsAvailable =
    getIsFeatureHasAnOption('cigarette_vacation_options', 'on') &&
    variant !== 'hotel' &&
    destinationValue?.groupName !== 'hotels';

  const { vacationOptionsPresets } = useFetchVacationOptionsPresets(isVacationOptionsAvailable);

  const { unavailableDates, arrivalDatesDict } = useFetchUnavailableDates({
    toCountry: destinationValue?.iso2,
    fromCity: departureValue?.value,
    searchType,
  });
  const { searchCalendarDuration, changeSearchCalendarDurationParams } =
    useFetchReferencesSearchCalendarDuration();

  const onPanelChange = useCallback(
    (value: { left: Date | null; right: Date | null }) => {
      if (value.left && departureValue?.id && destinationValue?.value) {
        changeSearchCalendarDurationParams(
          value.left,
          destinationValue.value,
          departureValue.id,
          touristsValue.adults,
          searchType
        );
      }
    },
    [departureValue?.id, touristsValue.adults, searchType, destinationValue?.value]
  );
  const openInWizard = isDatepickerStep(steps) || undefined;
  const openCalendar = !isMobile && !!error;
  const isOpen = openInWizard || openCalendar || isOpenInitial;

  const hideDatePicker =
    variant !== 'hotel' &&
    ((!isWizard && hide) || (isWizard && !openInWizard && !afterDatepickerStep(steps)));

  const onClose = useCallback(() => {
    setError(undefined);
  }, [setError]);

  const onChange = (newValue: DatePickerState) => {
    setValue(newValue);
    if (isWizard) {
      dispatch(addStep(FIELD_NAMES.tourists));
    }
  };

  useEffect(() => {
    if (hide && destinationValue) {
      setHide(false);
    }
  }, [destinationValue]);

  useEffect(() => {
    if (value && previousSearchType && previousSearchType !== searchType && variant !== 'hotel') {
      setValue(undefined);
    }
    if (previousSearchType !== searchType) {
      setPreviousSearchType(searchType);
    }
  }, [searchType]);

  if (hideDatePicker) {
    return null;
  }

  return (
    <FormDatePickerContainer
      $isWizard={isWizard}
      $isMobile={isMobile}
      $isTouristsStep={isTouristsStep(steps)}
    >
      <DatePicker
        onPanelChange={onPanelChange}
        onChange={onChange}
        isMobile={isMobile}
        isWizard={isWizard}
        initialValue={value}
        className={className}
        searchCalendarDuration={searchCalendarDuration}
        open={isOpen}
        onClose={onClose}
        unavailableDates={unavailableDates}
        arrivalDatesDict={arrivalDatesDict}
        searchType={searchType}
        variant={variant}
        departureId={departureValue?.id}
        placeId={destinationValue?.value}
        vacationOptionsPresets={vacationOptionsPresets}
        isOpenInitial={isOpenInitial}
      />
    </FormDatePickerContainer>
  );
};

const FormDatePickerContainer = styled.div<{
  $isWizard?: boolean;
  $isTouristsStep?: boolean;
  $isMobile?: boolean;
}>`
  width: ${({ $isMobile }) => ($isMobile ? '100%' : '350px')};
  ${({ $isTouristsStep, $isMobile }) => ($isTouristsStep || $isMobile ? `order: 1;` : '')};
`;
